/**
 * The row field values that cross the wire as typed shapes: the row kinds, a
 * playlist's timing, and the part and piece fields a playout instance carries
 * (`instances.ts`). Wire copies of `@norna/domain/fields.js` (D37: a contract
 * package never reuses a domain schema); `FIELD_SHAPE_PROOFS` fails to compile
 * the day the two drift apart, and the mappers are the place a difference is
 * then converted.
 *
 * A row's own `f` travels as an open record (`rowDocument.ts`), so a field a
 * newer writer adds reaches an older reader intact (PLAN §6.8); only these
 * snapshot shapes are typed field by field.
 */
import type {
  PartFields,
  PieceEnable,
  PieceFields,
  PieceLifespan,
  PlaylistTiming,
  RowKind,
} from '@norna/domain/fields.js';
import { durationMsSchema, epochMsSchema, jsonValueSchema, ordSchema, ulidSchema } from '@norna/domain/primitives.js';
import { z } from 'zod';
import type { Same } from './shapeProofs.js';

export const rowKindWireSchema = z.enum(['playlist', 'rundown', 'segment', 'part', 'piece', 'adlib']);
type RowKindWire = z.infer<typeof rowKindWireSchema>;

const pieceLifespanWireSchema = z.enum(['part-only', 'segment-change', 'segment-end', 'rundown-change', 'rundown-end', 'showstyle-end']);

const nameSchema = z.string().max(500);
const layerIdSchema = z.string().max(128);

/** PLAN §5.1: `enable {start: ms|'now', duration?}`, relative to the part start. */
const pieceEnableWireSchema = z
  .object({
    start: z.union([durationMsSchema, z.literal('now')]),
    duration: durationMsSchema.nullable(),
  })
  .strict();

/** PLAN §5.1: `inTransition {blockTakeDuration, previousPartKeepalive, contentDelay}`. */
const inTransitionWireSchema = z
  .object({
    blockTakeDuration: durationMsSchema,
    previousPartKeepalive: durationMsSchema,
    contentDelay: durationMsSchema,
  })
  .strict();

/** PLAN §5.1: `outTransition {duration}`. */
const outTransitionWireSchema = z.object({ duration: durationMsSchema }).strict();

/**
 * Playlist timing (PLAN §5.1: `none | forward-time(expectedStart) |
 * back-time(expectedEnd) | duration`), as Sofie's `RundownPlaylistTiming`:
 * the mode's anchor is required, the other two values are optional hints.
 */
export const playlistTimingWireSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }).strict(),
  z
    .object({
      type: z.literal('forward-time'),
      expectedStart: epochMsSchema,
      expectedDuration: durationMsSchema.nullable(),
      expectedEnd: epochMsSchema.nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('back-time'),
      expectedEnd: epochMsSchema,
      expectedStart: epochMsSchema.nullable(),
      expectedDuration: durationMsSchema.nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('duration'),
      expectedDuration: durationMsSchema,
      expectedEnd: epochMsSchema.nullable(),
    })
    .strict(),
]);
type PlaylistTimingWire = z.infer<typeof playlistTimingWireSchema>;

/** The registers every kind has. */
const structuralFields = {
  parentId: ulidSchema.nullable().default(null),
  ord: ordSchema.default('a0'),
  deleted: z.boolean().default(false),
};

export const partFieldsWireSchema = z.object({
  ...structuralFields,
  title: nameSchema.default(''),
  expectedDuration: durationMsSchema.nullable().default(null),
  autoNext: z.boolean().default(false),
  autoNextOverlap: durationMsSchema.default(0),
  untimed: z.boolean().default(false),
  invalid: z.boolean().default(false),
  invalidReason: z.string().max(1000).nullable().default(null),
  floated: z.boolean().default(false),
  inTransition: inTransitionWireSchema.nullable().default(null),
  outTransition: outTransitionWireSchema.nullable().default(null),
  script: z.string().default(''),
  notes: z.string().default(''),
});
type PartFieldsWire = z.infer<typeof partFieldsWireSchema>;

export const pieceFieldsWireSchema = z.object({
  ...structuralFields,
  name: nameSchema.default(''),
  pieceType: z.string().max(128).default(''),
  sourceLayer: layerIdSchema.default(''),
  outputLayer: layerIdSchema.default(''),
  params: z.record(z.string(), jsonValueSchema).default({}),
  prerollDuration: durationMsSchema.default(0),
  postrollDuration: durationMsSchema.default(0),
  lifespan: pieceLifespanWireSchema.default('part-only'),
  enable: pieceEnableWireSchema.default({ start: 0, duration: null }),
  virtual: z.boolean().default(false),
});
type PieceFieldsWire = z.infer<typeof pieceFieldsWireSchema>;

const rowKindSame: Same<RowKindWire, RowKind> = true;
const lifespanSame: Same<z.infer<typeof pieceLifespanWireSchema>, PieceLifespan> = true;
const enableSame: Same<z.infer<typeof pieceEnableWireSchema>, PieceEnable> = true;
const timingSame: Same<PlaylistTimingWire, PlaylistTiming> = true;
const partSame: Same<PartFieldsWire, PartFields> = true;
const pieceSame: Same<PieceFieldsWire, PieceFields> = true;
/** Compile-time proofs that each wire copy above matches its domain type (`shapeProofs.ts`). */
export const FIELD_SHAPE_PROOFS = [rowKindSame, lifespanSame, enableSame, timingSame, partSame, pieceSame] as const;
